import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageContainer } from '@/components/PageContainer';
import { toaster } from '@/components/ui/toaster';
import { trpc } from '@/lib/trpc';
import { useInformWriteJobResultPolling } from '@/lib/useInformWriteJobResultPolling';
import { Button } from '@televet/kibble-ui/Button';
import { Menu } from '@televet/kibble-ui/Menu';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Card } from '@televet/kibble-ui/Card';
import { Alert } from '@televet/kibble-ui/Alert';
import { Tag } from '@televet/kibble-ui/Tag';
import { Modal } from '@televet/kibble-ui/Modal';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@televet/kibble-ui';

export const Route = createFileRoute('/_authenticated/users/')({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Users',
    };
  },
});

type UserSource = 'flow' | 'inform';
const ALL_SOURCES: UserSource[] = ['flow', 'inform'];

function RouteComponent() {
  const [search, setSearch] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [selectedSources, setSelectedSources] = useState<UserSource[]>(ALL_SOURCES);
  const [page, setPage] = useState(0);
  const [impersonationUrl, setImpersonationUrl] = useState<string | null>(null);
  const [roleConfirm, setRoleConfirm] = useState<{
    email: string;
    action: 'add' | 'remove';
  } | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [deletingUserKey, setDeletingUserKey] = useState<string | null>(null);
  const { isPollingInformWriteJob, resolveInformWriteResult } = useInformWriteJobResultPolling();
  const queryClient = useQueryClient();
  const isProduction = import.meta.env.VITE_APP_ENV === 'production';
  const {
    data: usersData,
    isLoading,
    isFetching,
  } = useQuery(
    trpc.user.list.queryOptions({
      search: search || undefined,
      limit: 20,
      page,
      sources: selectedSources,
      clinicId: clinicId.trim() || undefined,
    })
  );

  type User = NonNullable<typeof usersData>['users'][number];
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { data: adminUsersData } = useQuery(trpc.user.listAdminUsers.queryOptions());

  const adminEmails = useMemo(() => {
    const emails = new Set<string>();
    if (adminUsersData?.users) {
      for (const user of adminUsersData.users) {
        if (user.roles.some((role) => role.name === 'admin')) {
          emails.add(user.email.toLowerCase());
        }
      }
    }
    return emails;
  }, [adminUsersData]);

  const sourcesKey = selectedSources.join(',');
  useEffect(() => {
    setPage(0);
  }, [search, sourcesKey, clinicId]);

  const { mutate: createTokenMutation, isPending: isCreatingToken } = useMutation(
    trpc.user.createImpersonationToken.mutationOptions({
      onSuccess: (data) => {
        const webAppUrl = import.meta.env.VITE_WEB_APP_URL || 'http://localhost:3003';
        const url = `${webAppUrl}/token-login?token=${data.customToken}&impersonate=true`;
        setImpersonationUrl(url);
      },
    })
  );

  const handleImpersonate = (user: User) => {
    setSelectedUser({ ...user });
    createTokenMutation({ targetUserEmail: user.email || '' });
  };

  const handleOpenImpersonation = () => {
    if (impersonationUrl) {
      window.open(impersonationUrl, '_blank');
      setTimeout(() => setImpersonationUrl(null), 500);
    }
  };

  const { mutateAsync: deleteFlowUser } = useMutation(
    trpc.user.deleteFlowUser.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Deleted Flow User with email ' + data.email,
          duration: 10000,
        });
        queryClient.invalidateQueries({ queryKey: [['user', 'list']] });
      },
      onError: (error) => {
        console.error(error);
        toaster.error({
          title: 'Failed to delete Flow User',
          description: error.message,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const { mutateAsync: deleteInformUser } = useMutation(
    trpc.user.deleteInformUser.mutationOptions({
      onError: (error) => {
        console.error(error);
        toaster.error({
          title: 'Failed to delete Inform User',
          description: error.message,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const { mutate: updateRole, isPending: isUpdatingRole } = useMutation(
    trpc.user.updateUserRole.mutationOptions({
      onSuccess: (data) => {
        setRoleConfirm(null);
        toaster.success({ title: data.message });
        queryClient.invalidateQueries({
          queryKey: [['user', 'listAdminUsers']],
        });
      },
      onError: (error) => {
        setRoleConfirm(null);
        toaster.error({
          title: 'Failed to update role',
          description: error.message,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const { mutateAsync: createPasswordlessUser, isPending: isCreatingPasswordlessUser } =
    useMutation(
      trpc.user.createFirebaseUser.mutationOptions({
        onSuccess: (data) => {
          setShowAddUser(false);
          setNewUserEmail('');
          toaster.success({
            title: 'Firebase user created',
            description: `Passwordless account created for ${data.email}`,
          });
          queryClient.invalidateQueries({ queryKey: [['user', 'list']] });
        },
        onError: (error) => {
          toaster.error({
            title: 'Failed to create user',
            description: error.message,
            duration: 10000,
            closable: true,
          });
        },
      })
    );

  const handleDeleteUser = async (user: User) => {
    if (!user.email) return;
    const sourceLabel = user.source === 'flow' ? 'Flow' : 'Inform';
    const confirm = window.confirm(
      `Are you absolutely sure you wish to **hard delete** this ${sourceLabel} user: ${user.email}?`
    );
    if (!confirm) return;

    const key = `${user.source}:${user.id}`;
    setDeletingUserKey(key);
    try {
      if (user.source === 'flow') {
        await deleteFlowUser({ email: user.email });
      } else {
        const deleteResult = await deleteInformUser({ email: user.email });
        const resolvedResult = await resolveInformWriteResult({
          initialResult: deleteResult,
          expectedOperation: 'deleteInformUser',
        });

        if (!resolvedResult) {
          return;
        }

        toaster.success({
          title: 'Deleted Inform User with email ' + resolvedResult.email,
          duration: 10000,
        });
        queryClient.invalidateQueries({ queryKey: [['user', 'list']] });
      }
    } finally {
      setDeletingUserKey(null);
    }
  };

  const handleNext = () => {
    if (usersData?.hasNext) setPage((p) => p + 1);
  };
  const handlePrevious = () => {
    setPage((p) => Math.max(0, p - 1));
  };

  const hasNext = !!usersData?.hasNext;
  const hasPrevious = page > 0;

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Alert
          status="warning"
          title="Admin Feature"
          description="Manage admin roles and impersonate Flow users for support and debugging. All impersonation sessions are logged."
          // Standing notice about what this page does, not something to dismiss.
          hideCloseButton
        />

        {impersonationUrl && selectedUser && (
          <Alert
            status="success"
            title="Impersonation Token Generated"
            description={`Ready to impersonate: ${selectedUser.email}${selectedUser.firstName ? ` (${selectedUser.firstName} ${selectedUser.lastName})` : ''}`}
            buttonProps={{
              buttonText: 'Open Web App as User',
              buttonPosition: 'bottomLeft',
              onClick: handleOpenImpersonation,
            }}
          />
        )}

        <Flex gap={2} align="center">
          <TextInput
            placeholder="Search by email, first name, last name, or clinic name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="lg"
            flex={1}
          />
          <TextInput
            placeholder="Filter by clinic ID"
            value={clinicId}
            onChange={(e) => setClinicId(e.target.value)}
            size="lg"
            w="280px"
          />
          <Menu
            buttonProps={{
              size: 'md',
              variant: 'secondary',
              iconName: 'filter',
              text:
                selectedSources.length === ALL_SOURCES.length
                  ? 'Source: All'
                  : selectedSources.length === 0
                    ? 'Source: None'
                    : `Source: ${selectedSources.map((s) => (s === 'flow' ? 'Flow' : 'Inform')).join(', ')}`,
            }}
            listProps={{
              menuItems: ALL_SOURCES.map((value) => ({
                type: 'checkbox',
                value,
                label: value === 'flow' ? 'Flow' : 'Inform',
                checked: selectedSources.includes(value),
              })),
              onSelect: ({ item }) => {
                const value = item.value as UserSource;
                setSelectedSources((prev) =>
                  prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
                );
              },
            }}
          />
          <Button
            size="md"
            variant="secondary"
            onClick={() => setShowAddUser(true)}
            iconName="personAdd"
          >
            Add Passwordless Firebase User
          </Button>
        </Flex>

        <Card
          flex="1 1 auto"
          direction="column"
          borderColor="border.default"
          rounded="lg"
          overflow="hidden"
        >
          <Table.Root size="lg">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Email</Table.ColumnHeader>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Source</Table.ColumnHeader>
                <Table.ColumnHeader>Clinics</Table.ColumnHeader>
                <Table.ColumnHeader>Role</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Flex justify="center" align="center" py={8}>
                      <Spinner size="lg" />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : usersData?.users.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Flex justify="center" align="center" py={8}>
                      <Text variant="subtle">No users found</Text>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : (
                usersData?.users.map((user) => {
                  const isFlow = user.source === 'flow';
                  const isAdmin = isFlow && adminEmails.has((user.email ?? '').toLowerCase());
                  return (
                    <Table.Row key={`${user.source}:${user.id}`}>
                      <Table.Cell>{user.email}</Table.Cell>
                      <Table.Cell>
                        {user.firstName || user.lastName
                          ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
                          : '-'}
                      </Table.Cell>
                      <Table.Cell>
                        <Tag
                          label={isFlow ? 'Flow' : 'Inform'}
                          variant={isFlow ? 'info' : 'warning'}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap={1} flexWrap="wrap">
                          {user.clinics.map((clinic) => (
                            <Tag key={clinic.id} label={clinic.name} variant="info" />
                          ))}
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>{isAdmin && <Tag label="Admin" variant="success" />}</Table.Cell>
                      <Table.Cell textAlign="end">
                        <Flex gap={2} justify="flex-end">
                          {isFlow && (
                            <>
                              <Button
                                iconName="personCircle"
                                size="sm"
                                variant="secondary"
                                onClick={() => handleImpersonate(user)}
                                loading={isCreatingToken}
                              />
                              {isAdmin ? (
                                <Button
                                  size="sm"
                                  variant="secondaryDestructive"
                                  onClick={() =>
                                    setRoleConfirm({
                                      email: user.email ?? '',
                                      action: 'remove',
                                    })
                                  }
                                >
                                  Revoke Admin
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() =>
                                    setRoleConfirm({
                                      email: user.email ?? '',
                                      action: 'add',
                                    })
                                  }
                                >
                                  Grant Admin
                                </Button>
                              )}
                            </>
                          )}
                          {!isProduction && (
                            <Button
                              variant="ghostDestructive"
                              onClick={() => handleDeleteUser(user)}
                              loading={
                                deletingUserKey === `${user.source}:${user.id}` ||
                                isPollingInformWriteJob
                              }
                              disabled={deletingUserKey !== null || isPollingInformWriteJob}
                            >
                              <Icon variant="danger" name="trashcan" />
                            </Button>
                          )}
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table.Root>
        </Card>

        {(hasNext || hasPrevious) && (
          <Flex justify="flex-end" align="center" gap={3} px={2}>
            <Button
              size="sm"
              disabled={!hasPrevious || isFetching}
              onClick={handlePrevious}
              loading={isFetching}
            >
              Previous
            </Button>
            <Button
              size="sm"
              disabled={!hasNext || isFetching}
              onClick={handleNext}
              loading={isFetching}
            >
              Next
            </Button>
          </Flex>
        )}
      </Flex>

      <ConfirmDialog
        isOpen={!!roleConfirm}
        title={roleConfirm?.action === 'add' ? 'Grant Admin Access' : 'Revoke Admin Access'}
        description={
          roleConfirm?.action === 'add'
            ? `This will grant ${roleConfirm.email} access to the admin panel. Are you sure?`
            : `This will remove ${roleConfirm?.email}'s access to the admin panel. Are you sure?`
        }
        confirmLabel={roleConfirm?.action === 'add' ? 'Grant Admin' : 'Revoke Admin'}
        variant={roleConfirm?.action === 'add' ? 'primary' : 'primaryDestructive'}
        onConfirm={() => {
          if (roleConfirm) {
            updateRole(roleConfirm);
          }
        }}
        onCancel={() => setRoleConfirm(null)}
        isLoading={isUpdatingRole}
      />

      <Modal
        open={showAddUser}
        onOpenChange={(details) => {
          if (!details.open) {
            setShowAddUser(false);
            setNewUserEmail('');
          }
        }}
        header="Add Passwordless Firebase User"
        footerProps={{
          submitText: 'Create User',
          cancelText: 'Cancel',
          onSubmit: async () => {
            await createPasswordlessUser({ email: newUserEmail });
          },
          onCancel: () => {
            setShowAddUser(false);
            setNewUserEmail('');
          },
          loading: isCreatingPasswordlessUser,
          disabled: !newUserEmail.includes('@'),
        }}
      >
        <Text mb={3} variant="subtle">
          Creates a Firebase Authentication account with no password. The user can sign in via email
          link.
        </Text>
        <TextInput
          placeholder="user@example.com"
          type="email"
          value={newUserEmail}
          onChange={(e) => setNewUserEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newUserEmail.includes('@')) {
              createPasswordlessUser({ email: newUserEmail });
            }
          }}
          autoFocus
        />
      </Modal>
    </PageContainer>
  );
}
